import React, { useState, useEffect, useRef } from 'react';
import { Trip, Day } from '../types';
import { TripPlannerAPI } from '../services/api';

interface Recommendation {
  id: string;
  name: string;
  placeName: string;
  description: string;
  type: string;
  location: {
    name: string;
    lat: number;
    lon: number;
  };
  estimatedCost: number;
  isLiked: boolean;
  isDisliked: boolean;
}

// Module-level cache to persist across component remounts
const recommendationsCache = new Map<string, Recommendation[]>();
const loadingCache = new Set<string>();
const lastApiCallTime = new Map<string, number>(); // Track when we last called API for each key
const MIN_API_CALL_INTERVAL = 5000; // Minimum 5 seconds between API calls for same key

const getCacheKey = (tripId: string, dayId: string) => `${tripId}:${dayId}`;

const clearCacheForKey = (key: string) => {
  recommendationsCache.delete(key);
  loadingCache.delete(key);
  lastApiCallTime.delete(key);
};

// Export function to clear cache externally (e.g., after adding activities)
export const clearRecommendationsCache = (tripId?: string, dayId?: string) => {
  if (tripId && dayId) {
    // Clear specific day
    const key = getCacheKey(tripId, dayId);
    clearCacheForKey(key);
    console.log('🗑️ Cleared recommendations cache for', key);
  } else {
    // Clear all
    recommendationsCache.clear();
    loadingCache.clear();
    console.log('🗑️ Cleared all recommendations cache');
  }
};

interface RecommendationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  day: Day;
  dayIndex: number;
  trip: Trip;
  onAddActivity: (dayIndex: number, recommendation: Recommendation) => void;
  onOpenManualAdd?: () => void; // Callback to open AddItineraryItemModal
  isLoading?: boolean;
}

const RecommendationsModal: React.FC<RecommendationsModalProps> = React.memo(({
  isOpen,
  onClose,
  day,
  dayIndex,
  trip,
  onAddActivity,
  onOpenManualAdd
}) => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [addedRecommendations, setAddedRecommendations] = useState<Set<string>>(new Set());
  const [addingRecommendationId, setAddingRecommendationId] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const hasInitializedRef = useRef(false); // Track if we've initialized in this mount cycle
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollIndex, setScrollIndex] = useState(0); // floating active index for smooth scaling
  const scrollRafRef = useRef<number | null>(null);
  
  // Drag state for desktop
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Load recommendations ONLY when modal opens - this is the ONLY place API is called
  useEffect(() => {
    // Only execute when modal transitions from closed to open
    if (isOpen) {
      const cacheKey = getCacheKey(trip.tripId, day.dayId);
      
      // PRIMARY GUARD: Check module-level cache FIRST (persists across component remounts)
      // This is the most important check - if data exists in cache, use it and skip everything
      const cachedData = recommendationsCache.get(cacheKey);
      if (cachedData) {
        console.log('🔄 Loading recommendations from cache for', cacheKey, '- skipping API call');
        setRecommendations(cachedData);
        setAddedRecommendations(new Set());
        setCurrentIndex(0);
        setShowFullDescription(false);
        hasInitializedRef.current = true;
        return; // Exit early - no API call needed
      }
      
      // SECONDARY GUARD: Check if already loading this specific day/trip
      if (loadingCache.has(cacheKey)) {
        console.log('⚠️ Already loading this day/trip, restoring loading state...');
        hasInitializedRef.current = true;
        // Restore loading state since API call is still in progress
        setLoadingRecommendations(true);
        setRecommendations([]); // Clear any stale data
        return; // Exit early - already loading
      }
      
      // TERTIARY GUARD: Rate limiting - prevent API calls within minimum interval
      const lastCallTime = lastApiCallTime.get(cacheKey);
      const now = Date.now();
      if (lastCallTime && (now - lastCallTime) < MIN_API_CALL_INTERVAL) {
        console.log(`⏱️ Rate limit: Last API call was ${Math.round((now - lastCallTime) / 1000)}s ago, skipping (min interval: ${MIN_API_CALL_INTERVAL / 1000}s)`);
        hasInitializedRef.current = true;
        // Try to get cached data again (in case it was just added)
        const rateLimitedCachedData = recommendationsCache.get(cacheKey);
        if (rateLimitedCachedData) {
          setRecommendations(rateLimitedCachedData);
          setAddedRecommendations(new Set());
          setCurrentIndex(0);
          setShowFullDescription(false);
        }
        return; // Exit early - rate limited
      }
      
      // QUATERNARY GUARD: Prevent re-initialization if already initialized in this mount
      // This is a backup - the cache should catch most cases
      if (hasInitializedRef.current) {
        console.log('🔒 Already initialized in this mount cycle, skipping');
        return; // Exit early - already initialized
      }
      
      console.log('🔄 Modal opened - loading recommendations for', cacheKey);
      
      // Mark as loading to prevent duplicate calls
      loadingCache.add(cacheKey);
      hasInitializedRef.current = true;
      
      // Reset UI state
      setAddedRecommendations(new Set());
      setCurrentIndex(0);
      setShowFullDescription(false);
      setLoadingRecommendations(true);
      
      // Capture current day and trip values at the moment modal opens
      const currentDay = day;
      const currentTrip = trip;
      const existingActivities = currentDay.itinerary?.map(item => item.name) || [];
      
      console.log('📡 Calling recommendations API...');
      
      // Record API call time BEFORE making the call
      lastApiCallTime.set(cacheKey, Date.now());
      
      // Make API call - this is the ONLY place the API is called
      TripPlannerAPI.generateRecommendations(
        currentTrip.tripId,
        currentDay.dayId,
        existingActivities
      )
        .then((response) => {
          if (response.success) {
            const apiRecommendations = response.recommendations.map((rec: any) => ({
              ...rec,
              isLiked: false,
              isDisliked: false
            }));
            // Store in both state and cache
            setRecommendations(apiRecommendations);
            recommendationsCache.set(cacheKey, apiRecommendations);
            console.log('✅ Recommendations loaded and cached:', apiRecommendations.length);
          } else {
            throw new Error(response.message || 'Failed to generate recommendations');
          }
        })
        .catch((error) => {
          console.error('❌ Error loading recommendations:', error);
          setRecommendations([]);
          // Remove from cache on error so it can be retried
          clearCacheForKey(cacheKey);
          hasInitializedRef.current = false; // Allow retry
        })
        .finally(() => {
          setLoadingRecommendations(false);
          loadingCache.delete(cacheKey);
        });
    } else {
      // When modal closes, reset initialization flag
      // But don't reset recommendations/loading if still loading (API call in progress)
      const cacheKey = getCacheKey(trip.tripId, day.dayId);
      const isStillLoading = loadingCache.has(cacheKey);
      
      console.log('🔒 Modal closed', isStillLoading ? '(API call still in progress)' : '');
      hasInitializedRef.current = false;
      
      // Only reset recommendations and loading state if not loading
      // This preserves the loading state if API call is still in progress
      if (!isStillLoading) {
        setRecommendations([]);
        setLoadingRecommendations(false);
      }
      setCurrentIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // ONLY depend on isOpen - day and trip are captured at open time

  const handleAddActivity = async (recommendation: Recommendation) => {
    if (addedRecommendations.has(recommendation.id) || addingRecommendationId === recommendation.id) return;
    
    // Set loading state for this specific recommendation
    setAddingRecommendationId(recommendation.id);
    
    try {
      // Create itinerary item from recommendation data
      const itemData = {
        name: recommendation.name,
        placeName: recommendation.placeName,
        itemType: recommendation.type.toLowerCase(),
        description: recommendation.description,
        location: {
          lat: recommendation.location.lat,
          lon: recommendation.location.lon
        },
        estimatedCost: parseFloat((recommendation.estimatedCost / 100).toFixed(2)), // Convert from cents to dollars
        startTime: "10:00", // Default time
        endTime: "12:00"     // Default time
      };

      // Call the existing add_item_to_day endpoint
      const response = await TripPlannerAPI.addItemToDay(
        trip.tripId,
        day.dayId,
        itemData
      );

      if (response.success) {
        // Call the parent handler to update the UI
        onAddActivity(dayIndex, response.item);
        
        // Mark this recommendation as added
        setAddedRecommendations(prev => new Set(prev).add(recommendation.id));
      } else {
        throw new Error(response.message || 'Failed to add activity');
      }
    } catch (error) {
      console.error('Error adding activity:', error);
      alert('Failed to add activity. Please try again.');
    } finally {
      // Clear loading state
      setAddingRecommendationId(null);
    }
  };

  // Smooth scroll handler using rAF to compute floating index for interpolation
  const handleScroll = React.useCallback(() => {
    if (!scrollContainerRef.current || containerWidth === 0) return;
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      if (!scrollContainerRef.current || containerWidth === 0) return;
      const container = scrollContainerRef.current;
      const scrollLeft = container.scrollLeft;
      const sideCardWidth = containerWidth * 0.50;
      const gap = 16;
      const padding = containerWidth * 0.125; // 12.5% padding on each side
      
      // Account for padding: scrollLeft + padding gives position relative to first card
      const adjustedScrollLeft = scrollLeft + padding;
      const cardUnit = sideCardWidth + gap;
      const floatIdx = cardUnit > 0 ? (adjustedScrollLeft / cardUnit) : 0;
      
      setScrollIndex(floatIdx);
      const rounded = Math.round(floatIdx);
      if (rounded !== currentIndex) {
        setCurrentIndex(rounded);
      }
    });
  }, [currentIndex, containerWidth]);
  
  // Cleanup rAF on unmount
  useEffect(() => {
    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  const scrollToIndex = React.useCallback((index: number) => {
    if (!scrollContainerRef.current || containerWidth === 0) return;
    const container = scrollContainerRef.current;
    
    // Card dimensions - use the measured containerWidth
    const sideCardWidth = containerWidth * 0.50; // 50% for side cards
    const gap = 16; // gap between cards
    const padding = containerWidth * 0.125; // 12.5% padding on each side
    
    // Calculate scroll position to center the selected card
    // Since cards have different widths, we need to calculate the cumulative position
    // For each card before the target, add its width + gap
    let cardPosition = 0;
    for (let i = 0; i < index; i++) {
      // Previous cards are side cards (50% width) when not centered
      cardPosition += sideCardWidth + gap;
    }
    
    // The scroll position needs to account for padding: cardPosition - padding
    // This positions the card so that when scrolled, it will be centered
    const scrollPosition = cardPosition - padding;
    
    container.scrollTo({
      left: Math.max(0, scrollPosition),
      behavior: 'smooth'
    });
  }, [containerWidth]);

  // Drag handlers for desktop
  const handleMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Don't start drag if clicking on a button or interactive element
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input')) {
      return;
    }

    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const startPos = { x: e.pageX, y: e.pageY };
    dragStartPosRef.current = startPos;
    setStartX(e.pageX - container.getBoundingClientRect().left);
    setScrollLeft(container.scrollLeft);
    
    // Small delay to check if it's a drag or click
    const checkDrag = (moveEvent: MouseEvent) => {
      const moveDistance = Math.abs(moveEvent.pageX - startPos.x) + Math.abs(moveEvent.pageY - startPos.y);
      if (moveDistance > 5) { // 5px threshold to distinguish drag from click
        setIsDragging(true);
        document.removeEventListener('mousemove', checkDrag);
        document.removeEventListener('mouseup', cleanup);
      }
    };
    
    const cleanup = () => {
      document.removeEventListener('mousemove', checkDrag);
      document.removeEventListener('mouseup', cleanup);
      if (!isDragging) {
        dragStartPosRef.current = null;
      }
    };
    
    document.addEventListener('mousemove', checkDrag);
    document.addEventListener('mouseup', cleanup);
  }, [isDragging]);


  const handleMouseLeave = React.useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      dragStartPosRef.current = null;
    }
  }, [isDragging]);

  // Global mouse event listeners for dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging || !scrollContainerRef.current || !dragStartPosRef.current) return;
      
      e.preventDefault();
      const container = scrollContainerRef.current;
      const rect = container.getBoundingClientRect();
      const x = e.pageX - rect.left;
      const walk = (x - startX) * 1.5; // Multiply by 1.5 for faster scrolling
      container.scrollLeft = scrollLeft - walk;
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        dragStartPosRef.current = null;
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, startX, scrollLeft]);

  // Measure container width and update on resize
  useEffect(() => {
    if (!contentContainerRef.current) return;
    
    const updateWidth = () => {
      if (contentContainerRef.current) {
        // Use clientWidth to get the actual content area width (excluding padding)
        // This gives us the width of the scrollable content area
        setContainerWidth(contentContainerRef.current.clientWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isOpen]);

  // Center the first card when recommendations are loaded
  useEffect(() => {
    if (recommendations.length > 0 && scrollContainerRef.current && containerWidth > 0) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        scrollToIndex(0);
      }, 100);
    }
  }, [recommendations.length, containerWidth, scrollToIndex]);

  const formatCost = (costInCents: number): string => {
    return `$${(costInCents / 100).toFixed(2)}`;
  };

  const getItemEmoji = (type: string) => {
    const emojiMap: Record<string, string> = {
      'restaurant': '🍽️',
      'attraction': '🏛️',
      'shopping': '🛍️',
      'activity': '🎯',
      'entertainment': '🎭',
      'transportation': '🚗',
      'lodging': '🏨',
      'museum': '🖼️',
    };
    return emojiMap[type.toLowerCase()] || '📍';
  };

  // Get day description text
  const dayDescription = day.summary || 'No description available for this day.';
  
  // Truncate description to ensure "Show more" fits on line 2
  // Estimate: ~60-80 chars per line for small text, so truncate to ~100-120 chars to leave room for "Show more" on line 2
  const getTruncatedDescription = (text: string) => {
    // Truncate if text is longer than ~120 chars (approximately 1.5-2 lines)
    if (text.length <= 120) return text;
    // Find a good break point (space or punctuation) near 100-110 chars
    const targetLength = 110;
    const truncated = text.substring(0, targetLength);
    const lastSpace = truncated.lastIndexOf(' ');
    const lastPunct = Math.max(
      truncated.lastIndexOf('.'),
      truncated.lastIndexOf(','),
      truncated.lastIndexOf('!'),
      truncated.lastIndexOf('?')
    );
    const breakPoint = Math.max(lastSpace, lastPunct);
    const finalBreak = breakPoint > 80 ? breakPoint : targetLength;
    return text.substring(0, finalBreak) + '...';
  };
  
  const needsTruncation = dayDescription.length > 120;
  const truncatedDescription = showFullDescription 
    ? dayDescription 
    : (needsTruncation ? getTruncatedDescription(dayDescription) : dayDescription);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={(e) => {
        // Only close on backdrop click, not on modal content
        if (e.target === e.currentTarget) {
          // Do nothing - prevent closing on backdrop click
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Close Button */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-[#E3E8E5] flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#0B1412]">Add New Activity</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F2FBF9] transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-[#6F7974]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div ref={contentContainerRef} className="flex-1 overflow-y-auto px-6 py-4">
          {/* Day Description */}
          <div className="mb-6">
            <div className="text-[#6F7974] text-sm leading-relaxed">
              <span className="text-[#0B1412] font-semibold">Day Summary: </span>
              <span>
                {truncatedDescription}
                {needsTruncation && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="text-primary-600 font-medium hover:underline ml-1"
                  >
                    {showFullDescription ? 'Show less' : 'Show more'}
                  </button>
                )}
              </span>
            </div>
          </div>

          {/* Recommendations Carousel */}
          {loadingRecommendations ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="loading-spinner w-8 h-8 mb-4"></div>
              <span className="text-[#6F7974] text-sm">Loading recommendations...</span>
            </div>
          ) : recommendations.length > 0 ? (
            <>
              {/* Carousel Container */}
              <div 
                ref={scrollContainerRef}
                onScroll={handleScroll}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory select-none"
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  paddingLeft: containerWidth > 0 ? `${containerWidth * 0.125}px` : '12.5%',
                  paddingRight: containerWidth > 0 ? `${containerWidth * 0.125}px` : '12.5%',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none'
                }}
              >
                {recommendations.map((recommendation, index) => {
                  const roundedIndex = Math.round(scrollIndex);
                  const isMain = index === roundedIndex;
                  const isAdded = addedRecommendations.has(recommendation.id);
                  // Using scrollIndex for dynamic transforms; container ref is maintained above
                  // distance from center in "cards"
                  const distance = Math.abs(scrollIndex - index);
                  const clamped = Math.min(distance, 1);
                  const scale = 0.9 + (1 - clamped) * 0.15; // 0.90 -> 1.05
                  const opacity = 0.6 + (1 - clamped) * 0.4; // 0.6 -> 1.0
                  const shadow = isMain ? '0 12px 28px rgba(0,0,0,0.15)' : '0 8px 16px rgba(0,0,0,0.08)';
                  
                  // Calculate card width based on container width
                  // Center card: 75% of container, side cards: 50% of container
                  const baseWidth = containerWidth > 0 
                    ? (isMain ? containerWidth * 0.75 : containerWidth * 0.50)
                    : (isMain ? '75%' : '50%');
                  
                  return (
                    <div
                      key={recommendation.id}
                      className={`flex-shrink-0 bg-white rounded-2xl border border-[#E3E8E5] shadow-lg transition-all duration-300 snap-center flex flex-col ${
                        isMain ? 'p-6' : 'p-4'
                      }`}
                      style={{ 
                        width: typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth,
                        minWidth: typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth,
                        maxWidth: typeof baseWidth === 'number' ? `${baseWidth}px` : baseWidth,
                        transform: `scale(${scale.toFixed(3)})`,
                        opacity: opacity,
                        boxShadow: shadow,
                        transition: 'transform 0.12s ease-out, opacity 0.12s ease-out, box-shadow 0.2s ease',
                        scrollSnapAlign: 'center',
                        scrollSnapStop: 'always',
                        pointerEvents: 'auto'
                      }}
                    >
                      {/* Header: Title and Icon (matching itinerary item style) */}
                      <div className="flex items-start justify-between mb-3">
                        <h3 
                          className="text-[#0B1412] font-bold flex-1"
                          style={{ fontSize: isMain ? '20px' : '18px', fontWeight: 700 }}
                        >
                          {recommendation.name}
                        </h3>
                        {/* Item type icon - top right */}
                        <div className={`flex items-center justify-center flex-shrink-0 ml-2 ${
                          isMain ? 'w-8 h-8 text-xl' : 'w-7 h-7 text-lg'
                        }`}>
                          {getItemEmoji(recommendation.type)}
                        </div>
                      </div>

                      {/* Meta Tags: Cost (matching itinerary item style) */}
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span 
                          className="inline-flex items-center px-3 py-1.5 rounded-2xl border border-[#E3E8E5] text-[#0B1412]"
                          style={{ fontSize: '13px', fontWeight: 600, height: '32px' }}
                        >
                          {formatCost(recommendation.estimatedCost)}
                        </span>
                      </div>

                      {/* Description */}
                      {recommendation.description && (
                        <p 
                          className={`text-[#6F7974] leading-relaxed mb-4 line-clamp-3`}
                          style={{ 
                            fontSize: isMain ? '15px' : '14px',
                            lineHeight: '1.5'
                          }}
                        >
                          {recommendation.description}
                        </p>
                      )}

                      {/* Place Name (if available) */}
                      {recommendation.placeName && (
                        <p className="text-[#6F7974] text-sm mb-4">
                          📍 {recommendation.placeName}
                        </p>
                      )}

                      {/* Add Button - Pushed to bottom */}
                      <button
                        onClick={() => handleAddActivity(recommendation)}
                        disabled={isAdded || addingRecommendationId === recommendation.id}
                        className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 mt-auto ${
                          isAdded
                            ? 'bg-[#E3E8E5] text-[#6F7974] cursor-not-allowed'
                            : addingRecommendationId === recommendation.id
                            ? 'bg-primary-300 text-white cursor-not-allowed'
                            : 'bg-primary-500 text-white hover:bg-primary-600 shadow-md hover:shadow-lg'
                        }`}
                      >
                        {isAdded ? (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Added
                          </>
                        ) : addingRecommendationId === recommendation.id ? (
                          <>
                            <div className="loading-spinner w-4 h-4 border-2 border-white border-t-transparent"></div>
                            Adding...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Activity for Today
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Carousel Indicator Dots */}
              {recommendations.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {recommendations.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => scrollToIndex(index)}
                      className={`transition-all duration-200 rounded-full ${
                        index === currentIndex
                          ? 'w-2.5 h-2.5 bg-primary-500'
                          : 'w-2 h-2 bg-[#E3E8E5] hover:bg-primary-300'
                      }`}
                      aria-label={`Go to recommendation ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-[#E3E8E5] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💡</span>
              </div>
              <p className="text-lg font-semibold text-[#0B1412] mb-2">No recommendations available</p>
              <p className="text-sm text-[#6F7974]">Try adding more details to your day summary</p>
            </div>
          )}

          {/* Add Activity Manually Button */}
          <div className="mt-6 pt-6 border-t border-[#E3E8E5]">
            <button
              onClick={() => {
                if (onOpenManualAdd) {
                  onClose();
                  onOpenManualAdd();
                }
              }}
              className="w-full py-3 px-4 rounded-xl font-semibold text-sm bg-white border-2 border-primary-500 text-primary-600 hover:bg-primary-50 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Activity Manually
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if these specific values change
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.dayIndex === nextProps.dayIndex &&
    prevProps.day.dayId === nextProps.day.dayId &&
    prevProps.trip.tripId === nextProps.trip.tripId &&
    prevProps.isLoading === nextProps.isLoading
  );
});

RecommendationsModal.displayName = 'RecommendationsModal';

export default RecommendationsModal;
